import { memo, useMemo } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import {
  type Availability,
  GAMIFICATION_RULES,
  type GamificationRule,
} from '@didaskill/domain/gamification/shared';
import type { GamificationProgressAchievement } from '@didaskill/domain/gamification-progresses/types';
import type {
  ACHIEVEMENT_TYPES,
  QuestAchievementType,
} from '@didaskill/domain/user-achievements/constants';
import { Tag, Typography } from '@didaskill/ui/design-system';
import { gamificationEventsMessages, globalMessages } from '@didaskill/ui/intl';

import messages from './QuestList.messages';
import * as styles from './QuestList.styles';
import { Quest } from './quest/Quest';

type QuestItem = {
  type: ACHIEVEMENT_TYPES;
  progress: {
    total: number;
    completed: number;
  };
  requirements: {
    title: string;
    isCompleted: boolean;
  }[];
  availability: Availability;
};

const buildQuestItem = (
  type: ACHIEVEMENT_TYPES,
  value: GamificationProgressAchievement | null,
  intl: IntlShape,
): QuestItem | null => {
  const isQuestEmpty = !value;

  const rule = GAMIFICATION_RULES[type] as GamificationRule;

  if (!rule) {
    return null;
  }

  const level = rule.levels[0];

  const completed = !isQuestEmpty
    ? Object.values(value.events).reduce((count, e) => (e.validatedAt ? count + 1 : count), 0)
    : 0;

  return {
    type,
    progress: {
      total: level.events.length ?? 0,
      completed,
    },
    requirements: level.events.map((event) => ({
      title: intl.formatMessage(gamificationEventsMessages[type][event.type]?.DESCRIPTION),
      isCompleted: !isQuestEmpty && !!value.events[event.type]?.validatedAt,
    })),
    availability: rule.availability,
  };
};

type QuestListBaseProps = {
  progress: Record<QuestAchievementType, GamificationProgressAchievement | null>;
  accessStartDate: Date;
};

const QuestListBase = ({ progress, accessStartDate }: QuestListBaseProps) => {
  const intl = useIntl();

  const questTileList = useMemo(
    () =>
      Object.entries(progress)
        .map(([type, value]) => buildQuestItem(type as ACHIEVEMENT_TYPES, value, intl))
        .filter(Boolean)
        .sort(
          (a, b) =>
            new Date(a.availability.absolute.start).getTime() -
            new Date(b.availability.absolute.start).getTime(),
        ),
    [intl, progress],
  );

  return (
    <div css={styles.container}>
      <div css={styles.title}>
        <Typography variant={Typography.VARIANTS.HEADING_4}>
          <FormattedMessage {...messages.MEINE_QUESTS} />
        </Typography>
        <Tag label={intl.formatMessage(globalMessages.NEW)} />
      </div>
      <div css={styles.tilesContainer}>
        {questTileList.map((quest) => (
          <Quest
            key={quest.type}
            type={quest.type as QuestAchievementType}
            progress={quest.progress}
            requirements={quest.requirements}
            availability={quest.availability}
            accessStartDate={accessStartDate}
          />
        ))}
      </div>
    </div>
  );
};

QuestListBase.displayName = 'QuestList';

export const QuestList = memo(QuestListBase);
